import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, EMPTY } from 'rxjs';
import { catchError, finalize, switchMap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import {
  ParsedFileData,
  UploadFilesState,
  FileTotals,
} from '../models/file-upload.model';
import { UploadStatus } from '../enums/upload-status.enum';
import { CpFileParserService } from './cp-file-parser.service';
import { ObjectStorageService } from './object-storage.service';

export interface FileValidationError {
  fileName: string;
  message: string;
}

const initialState: UploadFilesState = {
  parsedFiles: [],
  isUploading: false,
  totals: {
    totalCheckCount: 0,
    totalDdaCount: 0,
    totalDocsInFile: 0,
    totalAmount: 0,
  },
};

@Injectable({ providedIn: 'root' })
export class FileUploadService {
  private readonly parser = inject(CpFileParserService);
  private readonly objectStorage = inject(ObjectStorageService);
  private readonly stateSubject = new BehaviorSubject<UploadFilesState>(
    initialState
  );

  readonly uploadState$ = this.stateSubject.asObservable();

  private arNumber = '';
  private requestGuid = '';

  // ─── Getters ────────────────────────────────────────────────────────────────

  get parsedFiles(): ParsedFileData[] {
    return this.stateSubject.value.parsedFiles;
  }

  get totals(): FileTotals {
    return this.stateSubject.value.totals;
  }

  get slaFromFiles(): boolean {
    return this.stateSubject.value.parsedFiles.some(f => f.sla);
  }

  get isSlaFeeWaivedFromFiles(): boolean {
    return this.stateSubject.value.parsedFiles.some(f => f.isSlaFeeWaived);
  }

  get currentFileCount(): number {
    return this.stateSubject.value.parsedFiles.length;
  }

  /**
   * True when every valid file has been successfully uploaded to Swift.
   * Used by the wizard to gate advancing past Step 2.
   */
  get canProceedFromUploadStep(): boolean {
    const files = this.stateSubject.value.parsedFiles;
    if (files.length === 0) return false;
    const hasInFlight = files.some(
      f =>
        f.uploadStatus === UploadStatus.Pending ||
        f.uploadStatus === UploadStatus.Uploading
    );
    if (hasInFlight) return false;
    const validFiles = files.filter(f => f.isValid);
    if (validFiles.length === 0) return false;
    return validFiles.every(f => f.uploadStatus === UploadStatus.Uploaded);
  }

  isDuplicate(fileName: string): boolean {
    // Compare against sanitized name since that is what gets stored
    const sanitized = this.objectStorage.sanitizeFileName(fileName);
    return this.stateSubject.value.parsedFiles.some(
      f => f.fileName.toLowerCase() === sanitized.toLowerCase()
    );
  }

  getRequestGuid(): string {
    return this.requestGuid;
  }

  // ─── Session context ────────────────────────────────────────────────────────

  /**
   * Set AR number and request GUID before files are uploaded.
   * Called by the wizard when advancing from Step 1 to Step 2.
   */
  setRequestContext(arNumber: string, requestGuid: string): void {
    this.arNumber = arNumber;
    this.requestGuid = requestGuid;
  }

  // ─── File operations ────────────────────────────────────────────────────────

  /**
   * Add a file: parse → upload to Swift temp path.
   * Status flow: Pending → Uploading → Uploaded | Failed
   * Invalid files (parse errors, country mismatch) stay as Failed and are not uploaded.
   */
  addFile(file: File): void {
    const fileId = `file-${uuidv4()}`;
    const sanitizedName = this.objectStorage.sanitizeFileName(file.name);

    const initialEntry: ParsedFileData = {
      fileId,
      fileName: sanitizedName,
      originalFileName: file.name,
      countryCode: '',
      country: '',
      uploadStatus: UploadStatus.Pending,
      uploadProgress: 0,
      checkCount: 0,
      ddaCount: 0,
      docsInFile: 0,
      totalAmount: 0,
      sla: false,
      isSlaFeeWaived: false,
      isValid: false,
    };

    this.updateState(state => ({
      ...state,
      parsedFiles: [...state.parsedFiles, initialEntry],
      isUploading: true,
    }));

    this.parser
      .parseFile(file, fileId)
      .pipe(
        switchMap(parsed => {
          // Country mismatch check against existing valid files
          const existing = this.stateSubject.value.parsedFiles;
          const firstValidCountry = existing.find(
            f => f.isValid && f.fileId !== fileId
          )?.countryCode;

          let entry = {
            ...parsed,
            fileId,
            fileName: sanitizedName,
            uploadProgress: 0,
          };

          if (
            entry.isValid &&
            firstValidCountry &&
            entry.countryCode !== firstValidCountry
          ) {
            entry = {
              ...entry,
              isValid: false,
              uploadStatus: UploadStatus.Failed,
              validationMessage: `This file is for ${entry.country} but the request already contains ${firstValidCountry} files. All files in a request must be for the same country.`,
            };
          }

          const entryStatus = entry.isValid
            ? UploadStatus.Uploading
            : UploadStatus.Failed;
          entry = { ...entry, uploadStatus: entryStatus };

          this.updateParsedFile(fileId, () => entry);

          this.updateState(state => ({
            ...state,
            totals: this.calculateTotals(state.parsedFiles),
          }));

          if (!entry.isValid) {
            return EMPTY;
          }

          const swiftPath = this.objectStorage.buildTempPath(
            this.arNumber,
            this.requestGuid,
            file.name
          );

          return this.objectStorage.uploadFile(swiftPath, file).pipe(
            catchError(err => {
              const msg = (err as Error).message ?? 'Upload to storage failed';
              this.updateParsedFile(fileId, f => ({
                ...f,
                uploadStatus: UploadStatus.Failed,
                uploadProgress: 0,
                validationMessage: msg,
              }));
              return EMPTY;
            })
          );
        }),
        catchError(err => {
          // Parse error
          this.updateParsedFile(fileId, f => ({
            ...f,
            uploadStatus: UploadStatus.Failed,
            validationMessage: (err as Error).message,
          }));
          return EMPTY;
        }),
        finalize(() => this.refreshUploadingFlag())
      )
      .subscribe(progress => {
        if (progress.done) {
          const swiftPath = this.objectStorage.buildTempPath(
            this.arNumber,
            this.requestGuid,
            file.name
          );
          this.updateParsedFile(fileId, f => ({
            ...f,
            uploadStatus: UploadStatus.Uploaded,
            uploadProgress: 100,
            swiftPath,
          }));
        } else {
          this.updateParsedFile(fileId, f => ({
            ...f,
            uploadStatus: UploadStatus.Uploading,
            uploadProgress: progress.percent,
          }));
        }
      });
  }

  /**
   * Remove a file: delete from Swift (fire-and-forget) then remove from state.
   */
  removeFile(fileId: string): void {
    const entry = this.stateSubject.value.parsedFiles.find(
      f => f.fileId === fileId
    );
    if (entry?.swiftPath) {
      this.objectStorage
        .deleteFile(entry.swiftPath)
        .subscribe({ error: () => {} }); // eslint-disable-line @typescript-eslint/no-empty-function
    }

    this.updateState(state => {
      const newFiles = state.parsedFiles.filter(f => f.fileId !== fileId);
      return {
        ...state,
        parsedFiles: newFiles,
        totals: this.calculateTotals(newFiles),
      };
    });
  }

  /**
   * Cancel the wizard session: delete the entire temp directory from Swift,
   * then reset all local state.
   * Call this on wizard cancel/close and when going back from Step 2 to Step 1.
   */
  cancelSession(): void {
    if (this.arNumber && this.requestGuid) {
      const dirPath = this.objectStorage.buildSessionDirPath(
        this.arNumber,
        this.requestGuid
      );
      this.objectStorage
        .deleteDirectory(dirPath)
        .subscribe({ error: () => {} }); // eslint-disable-line @typescript-eslint/no-empty-function
    }
    this.arNumber = '';
    this.requestGuid = '';
    this.stateSubject.next(initialState);
  }

  // ─── Private helpers ────────────────────────────────────────────────────────

  private updateParsedFile(
    fileId: string,
    updater: (f: ParsedFileData) => ParsedFileData
  ): void {
    this.updateState(state => ({
      ...state,
      parsedFiles: state.parsedFiles.map(f =>
        f.fileId === fileId ? updater(f) : f
      ),
    }));
  }

  private refreshUploadingFlag(): void {
    const state = this.stateSubject.value;
    const isUploading = state.parsedFiles.some(
      f =>
        f.uploadStatus === UploadStatus.Pending ||
        f.uploadStatus === UploadStatus.Uploading
    );
    if (state.isUploading !== isUploading) {
      this.updateState(s => ({ ...s, isUploading }));
    }
  }

  private calculateTotals(parsedFiles: ParsedFileData[]): FileTotals {
    return parsedFiles
      .filter(f => f.isValid)
      .reduce(
        (acc, file) => ({
          totalCheckCount: acc.totalCheckCount + file.checkCount,
          totalDdaCount: acc.totalDdaCount + file.ddaCount,
          totalDocsInFile: acc.totalDocsInFile + file.docsInFile,
          totalAmount: acc.totalAmount + file.totalAmount,
        }),
        {
          totalCheckCount: 0,
          totalDdaCount: 0,
          totalDocsInFile: 0,
          totalAmount: 0,
        }
      );
  }

  private updateState(
    updater: (state: UploadFilesState) => UploadFilesState
  ): void {
    this.stateSubject.next(updater(this.stateSubject.value));
  }
}
